import { useDepartmentText } from 'model/DepartmentTerminology';
import { Badge, Box, Group, Tabs, Tooltip } from '@mantine/core';
import DeviceFileTabContent from 'components/forms/device/DeviceFilesTabContent';
import { IconDownload, IconPlus } from '@tabler/icons-react';
import { randomId } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAddDeviceFile } from 'components/modals/add-device-file/AddDeviceFileProvider';
import { DeviceFormData } from 'components/forms/device/DeviceForm';
import { UseFormReturnType } from '@mantine/form';
import { useFileManager } from 'api/file-manager/file-manager-context';
import useFileDownload from 'api/use-file-download';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { FEATURE_TOGGLE_DOWNLOAD_ALL_DEVICE_FILES } from 'env';
import {
  DEVICE_FILE_FORMS_TAB,
  DEVICE_FILE_TYPE_NAME_MAP,
  DeviceFileTabValue,
  DeviceFileType,
  getDeviceFileTypeAccessPermission,
  isFormFileType,
  isRevisionFileType,
  isUploadableDeviceFileType,
} from 'model/DeviceFileType';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

import Button from 'components/Button';
interface DeviceFilesProps {
  deviceId: number;
  deviceName: string;
  departmentId?: number;
  context?: string;
  form?: UseFormReturnType<DeviceFormData>;
  files: DeviceFormData['files'];
  readonly?: boolean;
  initialTab?: DeviceFileTabValue;
  persistActiveTab?: boolean;
  onVisibleTabsChange?: (count: number) => void;
}

const DEVICE_FILE_TYPES = Object.keys(DEVICE_FILE_TYPE_NAME_MAP) as DeviceFileType[];

/** Maps legacy and grouped file-tab values to their current visible tab. */
function normalizeDeviceFileTab(value: string): DeviceFileTabValue {
  if (value === 'signed-document' || value === 'revisions') {
    return 'revision';
  }

  if (value === 'form') {
    return DEVICE_FILE_FORMS_TAB;
  }

  return value as DeviceFileTabValue;
}

/** Creates a file-type permission map initialized with the provided default value. */
function createFileTypePermissions(defaultValue: boolean): Record<DeviceFileType, boolean> {
  return DEVICE_FILE_TYPES.reduce((acc, fileType) => {
    acc[fileType] = defaultValue;
    return acc;
  }, {} as Record<DeviceFileType, boolean>);
}

/**
 * Tab name component for the device files.
 */
function DeviceTab({
  activeTab,
  value,
  files,
  canManageFileType,
}: {
  activeTab: DeviceFileTabValue;
  value: DeviceFileTabValue;
  files: DeviceFormData['files'];
  canManageFileType: (fileType: DeviceFileType) => boolean;
}) {
  const departmentText = useDepartmentText();
  const tabName =
    value === DEVICE_FILE_FORMS_TAB
      ? 'Formuláre'
      : value === 'department'
      ? departmentText(DEVICE_FILE_TYPE_NAME_MAP.department, 'Ostatná dokumentácia zo strediska')
      : DEVICE_FILE_TYPE_NAME_MAP[value];
  const isActive = activeTab === value;
  const badgeColor = isActive ? 'red' : 'gray.5';
  const nFiles = files.filter((file) => {
    if (file.deleted || !canManageFileType(file.type)) {
      return false;
    }

    if (value === DEVICE_FILE_FORMS_TAB) {
      return isFormFileType(file.type);
    }

    if (value === 'revision') {
      return isRevisionFileType(file.type);
    }

    return file.type === value;
  }).length;

  return (
    <Tabs.Tab value={value}>
      <Group spacing={6} align="center">
        <span>{tabName}</span>
        <Badge color={badgeColor} size="sm" variant="filled" mt={2} px={5}>
          <Box pt={1}>{nFiles}</Box>
        </Badge>
      </Group>
    </Tabs.Tab>
  );
}

/**
 * Device files component, used to upload or display files.
 */
export default function DeviceFiles({
  deviceId,
  deviceName,
  departmentId,
  context = 'form',
  form,
  files,
  readonly = false,
  initialTab = 'project-documentation',
  persistActiveTab = true,
  onVisibleTabsChange,
}: DeviceFilesProps) {
  const departmentText = useDepartmentText();
  const { isMobile } = useResponsiveDesign();
  const { getAction } = useApi();
  const { getFileMetadata, buildZip } = useFileManager();
  const { addDeviceFile } = useAddDeviceFile();
  const key = useMemo(() => `Objekto.DeviceFiles.${context}.${deviceId}`, [context, deviceId]);
  const { download } = useFileDownload();
  const [activeTab, setActiveTab] = useState<DeviceFileTabValue>(() => {
    if (!persistActiveTab) {
      return normalizeDeviceFileTab(initialTab);
    }

    try {
      const value = localStorage.getItem(key) || '{}';
      const persisted = JSON.parse(value) as { activeTab?: DeviceFileTabValue };
      return normalizeDeviceFileTab(persisted.activeTab ?? initialTab);
    } catch (_error) {
      return normalizeDeviceFileTab(initialTab);
    }
  });
  const [loading, setLoading] = useState(false);
  const [fileTypePermissions, setFileTypePermissions] = useState<Record<DeviceFileType, boolean>>(() =>
    createFileTypePermissions(false)
  );
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    if (!persistActiveTab) {
      setActiveTab(normalizeDeviceFileTab(initialTab));
    }
  }, [initialTab, persistActiveTab, key]);

  useEffect(() => {
    if (!persistActiveTab) {
      return;
    }

    localStorage.setItem(key, JSON.stringify({ activeTab }));
  }, [activeTab, key, persistActiveTab]);

  useEffect(() => {
    let stale = false;
    setPermissionsLoaded(false);

    if (!departmentId) {
      setFileTypePermissions(createFileTypePermissions(false));
      setPermissionsLoaded(true);
      return () => {
        stale = true;
      };
    }

    const hasPermissionInDepartment = getAction('AuthUserHasPermissionInDepartment');

    Promise.all(
      DEVICE_FILE_TYPES.map(async (fileType) => {
        const { hasPermission } = await hasPermissionInDepartment({
          parameters: {
            departmentId: String(departmentId),
            permissionSlug: getDeviceFileTypeAccessPermission(fileType),
          },
        });

        return [fileType, hasPermission] as const;
      })
    )
      .then((entries) => {
        if (stale) {
          return;
        }

        const nextPermissions = createFileTypePermissions(false);
        entries.forEach(([fileType, hasPermission]) => {
          nextPermissions[fileType] = hasPermission;
        });

        setFileTypePermissions(nextPermissions);
        setPermissionsLoaded(true);
      })
      .catch((error: any) => {
        if (stale) {
          return;
        }

        panic(error);
        setFileTypePermissions(createFileTypePermissions(false));
        setPermissionsLoaded(true);
      });

    return () => {
      stale = true;
    };
  }, [departmentId, getAction]);

  const canManageFileType = useCallback(
    (fileType: DeviceFileType) => fileTypePermissions[fileType] ?? false,
    [fileTypePermissions]
  );

  const allowedRealTabs = useMemo(
    () =>
      (Object.keys(DEVICE_FILE_TYPE_NAME_MAP) as DeviceFileType[]).filter((fileType) => canManageFileType(fileType)),
    [canManageFileType]
  );

  const hasAllowedFormsTab = useMemo(
    () => allowedRealTabs.some((fileType) => isFormFileType(fileType)),
    [allowedRealTabs]
  );

  const visibleTabs = useMemo<DeviceFileTabValue[]>(() => {
    const tabs: DeviceFileTabValue[] = [];

    if (allowedRealTabs.includes('project-documentation')) {
      tabs.push('project-documentation');
    }

    if (allowedRealTabs.some(isRevisionFileType)) {
      tabs.push('revision');
    }

    if (hasAllowedFormsTab) {
      tabs.push(DEVICE_FILE_FORMS_TAB);
    }

    if (allowedRealTabs.includes('photos')) {
      tabs.push('photos');
    }

    if (allowedRealTabs.includes('other')) {
      tabs.push('other');
    }

    if (allowedRealTabs.includes('department')) {
      tabs.push('department');
    }

    return tabs;
  }, [allowedRealTabs, hasAllowedFormsTab]);

  /** Returns the default form file type to pre-fill when opening the modal from the forms tab. */
  const defaultFormFileType = useMemo<DeviceFileType | undefined>(
    () => allowedRealTabs.find(isFormFileType),
    [allowedRealTabs]
  );

  const uploadableFileTypes = useMemo(() => allowedRealTabs.filter(isUploadableDeviceFileType), [allowedRealTabs]);

  /** Adds the file to the device. */
  const addFile = () =>
    addDeviceFile({
      fileType: activeTab === DEVICE_FILE_FORMS_TAB ? defaultFormFileType : (activeTab as DeviceFileType),
      allowedFileTypes: uploadableFileTypes,
      onAdd: (newFiles, fileType) =>
        newFiles
          .filter(({ fileId }) => !!fileId)
          .forEach(({ fileId, fileName }, index) => {
            const hasNoPhotos = form!.values.files.filter((f) => f.type === 'photos' && !f.deleted).length === 0;
            const isMainImage = fileType === 'photos' && hasNoPhotos && index === 0;

            form!.insertListItem('files', {
              fileId,
              fileName,
              originalFileName: '',
              uploadedAt: new Date(),
              key: randomId(),
              deleted: false,
              isMainImage,
              type: fileType,
            });
          }),
    });

  /** Downloads all files */
  const downloadAllFiles = async () => {
    try {
      if (loading) {
        return;
      }

      setLoading(true);

      const filesToDownload = [];

      for (const { fileId, fileName, type } of files) {
        if (!canManageFileType(type)) {
          continue;
        }

        const metadata = await getFileMetadata({ fileId });

        const fileNameParts = fileName.split('.');
        const fileNameWithoutExtension = fileNameParts.length > 1 ? fileNameParts.slice(0, -1).join('.') : fileName;
        const fileNameWithExtension = `${fileNameWithoutExtension}${metadata.extension}`;

        const tabName =
          type === 'department'
            ? departmentText(DEVICE_FILE_TYPE_NAME_MAP.department, 'Ostatná dokumentácia zo strediska')
            : DEVICE_FILE_TYPE_NAME_MAP[type];
        const path = [tabName, fileNameWithExtension].join('/');

        filesToDownload.push({ fileId, path });
      }

      const fileId = await buildZip({
        files: filesToDownload,
        fileName: `${deviceId} ${deviceName}`,
        batchSize: 50,
      });

      await download(fileId);
    } catch (error: any) {
      panic(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visibleTabs.length === 0) {
      return;
    }

    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [activeTab, setActiveTab, visibleTabs]);

  useEffect(() => {
    if (!persistActiveTab) {
      return;
    }

    if (!deviceId && activeTab === 'department') {
      setActiveTab('project-documentation');
    }
  }, [deviceId, activeTab, persistActiveTab]);

  useEffect(() => {
    if (permissionsLoaded) {
      onVisibleTabsChange?.(visibleTabs.length);
    }
  }, [permissionsLoaded, visibleTabs.length, onVisibleTabsChange]);

  if (permissionsLoaded && visibleTabs.length === 0) {
    return null;
  }

  return (
    <>
      <Tabs
        value={activeTab}
        onTabChange={(nextActiveTab: DeviceFileTabValue) => setActiveTab(nextActiveTab)}
        styles={(theme) => ({
          tab: {
            '&[data-active]': {
              boxShadow: `inset 0 -2px 0 ${theme.colors.red[8]}`,
            },
          },
        })}
      >
        <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <Tabs.List sx={{ flexWrap: 'nowrap', minWidth: 'max-content' }}>
            {visibleTabs.includes('project-documentation') && (
              <DeviceTab
                files={files}
                activeTab={activeTab}
                value="project-documentation"
                canManageFileType={canManageFileType}
              />
            )}
            {visibleTabs.includes('revision') && (
              <DeviceTab files={files} activeTab={activeTab} value="revision" canManageFileType={canManageFileType} />
            )}
            {visibleTabs.includes(DEVICE_FILE_FORMS_TAB) && (
              <DeviceTab
                files={files}
                activeTab={activeTab}
                value={DEVICE_FILE_FORMS_TAB}
                canManageFileType={canManageFileType}
              />
            )}
            {visibleTabs.includes('photos') && (
              <DeviceTab files={files} activeTab={activeTab} value="photos" canManageFileType={canManageFileType} />
            )}
            {visibleTabs.includes('other') && (
              <DeviceTab files={files} activeTab={activeTab} value="other" canManageFileType={canManageFileType} />
            )}
            {visibleTabs.includes('department') && (
              <Tooltip
                withArrow={false}
                openDelay={300}
                multiline
                width={deviceId ? 220 : 285}
                label={
                  deviceId
                    ? departmentText(
                        'Súbory zdieľané medzi všetkými zariadeniami v položke úrovne',
                        'Súbory zdieľané medzi všetkými zariadeniami v stredisku'
                      )
                    : departmentText(
                        'Ostatnú dokumentáciu z položky úrovne bude možné nastaviť po vytvorení zariadenia',
                        'Ostatnú dokumentáciu zo strediska bude možné nastaviť po vytvorení zariadenia'
                      )
                }
                position="top"
              >
                <Box>
                  <DeviceTab
                    files={files}
                    activeTab={activeTab}
                    value="department"
                    canManageFileType={canManageFileType}
                  />
                </Box>
              </Tooltip>
            )}
            {FEATURE_TOGGLE_DOWNLOAD_ALL_DEVICE_FILES && !isMobile && (
              <Box pb={4} pr={4} ml="auto">
                <Button
                  size="sm"
                  variant="subtle"
                  leftIcon={<IconDownload />}
                  onClick={downloadAllFiles}
                  loading={loading}
                >
                  Stiahnuť všetky súbory
                </Button>
              </Box>
            )}
          </Tabs.List>
        </Box>
        {FEATURE_TOGGLE_DOWNLOAD_ALL_DEVICE_FILES && isMobile && (
          <Box pt={12}>
            <Button
              size="sm"
              variant="subtle"
              leftIcon={<IconDownload />}
              fullWidth
              onClick={downloadAllFiles}
              loading={loading}
            >
              Stiahnuť všetky súbory
            </Button>
          </Box>
        )}

        {visibleTabs.includes('project-documentation') && (
          <Tabs.Panel value="project-documentation">
            <DeviceFileTabContent
              tabName="project-documentation"
              form={form}
              readonly={readonly}
              files={files}
              canManageFileType={canManageFileType}
            />
          </Tabs.Panel>
        )}
        {visibleTabs.includes('revision') && (
          <Tabs.Panel value="revision">
            <DeviceFileTabContent
              tabName="revision"
              form={form}
              readonly={readonly}
              files={files}
              canManageFileType={canManageFileType}
            />
          </Tabs.Panel>
        )}
        {visibleTabs.includes(DEVICE_FILE_FORMS_TAB) && (
          <Tabs.Panel value={DEVICE_FILE_FORMS_TAB}>
            <DeviceFileTabContent
              tabName={DEVICE_FILE_FORMS_TAB}
              form={form}
              readonly={readonly}
              files={files}
              canManageFileType={canManageFileType}
            />
          </Tabs.Panel>
        )}
        {visibleTabs.includes('photos') && (
          <Tabs.Panel value="photos">
            <DeviceFileTabContent
              tabName="photos"
              form={form}
              readonly={readonly}
              files={files}
              canManageFileType={canManageFileType}
            />
          </Tabs.Panel>
        )}
        {visibleTabs.includes('other') && (
          <Tabs.Panel value="other">
            <DeviceFileTabContent
              tabName="other"
              form={form}
              readonly={readonly}
              files={files}
              canManageFileType={canManageFileType}
            />
          </Tabs.Panel>
        )}
        {visibleTabs.includes('department') && (
          <Tabs.Panel value="department">
            <DeviceFileTabContent
              tabName="department"
              form={form}
              readonly={readonly}
              files={files}
              canManageFileType={canManageFileType}
            />
          </Tabs.Panel>
        )}
      </Tabs>
      {!readonly && uploadableFileTypes.length > 0 && (
        <Button size="md" mt={24} variant="subtle" leftIcon={<IconPlus stroke="1.5" />} onClick={addFile}>
          Pridať súbory
        </Button>
      )}
    </>
  );
}
